import { catchError, } from "@bracket/catch";
import { getTheme, themed,Flow, Stream, Text as Tx, TextNode, } from "@bracket/ui";
import type { ErrorInfo, } from "react";
import { useEffect, } from "react";
import { isRouteErrorResponse, useRouteError, } from "react-router-dom";
import SyntaxHighlighter from 'react-syntax-highlighter';
import { docco } from 'react-syntax-highlighter/dist/esm/styles/hljs';
import styled from "styled-components";
import type { Config, } from "wagmi";
import { getAccount, } from "wagmi/actions";



export declare type CrashScreenProps = {
	error?: Error;
	errorInfo?: ErrorInfo;
	resetError?: () => void;
	config?: Config;
	boundry?: boolean;
}

export const CrashScreen = ({ error, errorInfo, resetError, config, boundry = false, }: CrashScreenProps) => {
	if(boundry) {
		return <CrashScreenInternal error={error} errorInfo={errorInfo} resetError={resetError} config={config} />;
	}
	return <CrashScreenWithRouterError error={error} errorInfo={errorInfo} resetError={resetError} config={config} />;
};



/**
 * CrashScreen component displays error information when the application crashes
 * Used with ErrorBoundary to show a fallback UI when JavaScript errors occur
 * 
 * @param props - The component props
 * @param props.error - The error object that was thrown
 * @param props.errorInfo - Additional error information from React
 * @param props.resetError - Function to reset the error state and retry
 */
const CrashScreenWithRouterError = ({ error, errorInfo, resetError, config, }: Omit<CrashScreenProps, "boundry">) => {
	const routerError = useRouteError();
	return <CrashScreenInternal error={error} errorInfo={errorInfo} resetError={resetError} config={config} routerError={routerError} />;
};

const CrashScreenInternal = ({ error, errorInfo, resetError, config, routerError, }: CrashScreenProps & { routerError?: unknown }) => {
	const isRouterError = isRouteErrorResponse(routerError);

	error = error instanceof Error ? error : routerError instanceof Error ? routerError : undefined;

	useEffect(() => {
		const { address, } = config ? getAccount(config) : { address: "0xunknown", };
		const { hostname, } = globalThis.location;
		if(hostname === "localhost") {
			return;
		}
		catchError({
			error: error || new Error("Unknown error"),
			system: "UI - hostname",
			code: "USAGE_CRASH",
			address,
			headers: {},
		});
	}, []);

	if(!isRouterError && !error) {
		console.error("bad Error Throw.... no valid error found.")
		return null;
	}

	const status = isRouterError ? routerError.status : "Invalid RPC Response";
	const message = isRouterError ? routerError.statusText : error instanceof Error ? error.message : "unknown error";
	const stack = isRouterError ? (typeof routerError.data === "string" ? routerError.data : JSON.stringify(routerError.data)) : error instanceof Error ? error.stack : "unknown error stack";

	return (
		<CrashContainer>
			<CrashContent>
				<Tx h2 color="heading">Unexpected Service Response</Tx>
				<Tx color="paragraph">Status: {status}</Tx>
				<Tx color="paragraph">The application encountered an unexpected response from the onchain RPC service. Please try refreshing the page as this normally fixes the issue.</Tx>
				{
					globalThis.location.hostname === "localhost" && (
						<ErrorDetails>
							<ErrorTitle variant="h4" color="var(--text-heading)">Error Details</ErrorTitle>
							<ErrorMessage variant="code" color="var(--text-error)">
								{message}
							</ErrorMessage>
							<SyntaxHighlighter language="javascript" style={docco}>
								{stack}
							</SyntaxHighlighter>
						</ErrorDetails>
					)
				}
				<ActionButtons>
					<RefreshButton onClick={() => window.location.reload()}>
						Refresh Page
					</RefreshButton>
				</ActionButtons>
			</CrashContent>
		</CrashContainer>
	);
};



const CrashContainer = styled(Flow)`
	align-items: center;
	justify-content: center;
	min-height: 100vh;
	padding: 2rem;
	background: #1f1f1f;
`;

const CrashContent = styled.div`
	max-width: 90%;
	width: 100%;
	text-align: center;
	padding: 3rem;
	border-radius: 12px;
	border: 1px solid var(--border-primary);
	background: var(--background-secondary);
`;


const CrashTitle = styled(TextNode)`
	margin-bottom: 1rem;
	white-space: nowrap;
`;

const CrashMessage = styled(TextNode)`
	margin-bottom: 2rem;
	line-height: 1.6;
`;

const ErrorDetails = styled.div`
	text-align: center;
	margin-bottom: 2rem;
	padding: 1.5rem;
	border-radius: 8px;
	background: var(--background-tertiary);
	border: 1px solid var(--border-secondary);
`;

const ErrorTitle = styled(TextNode)`
	margin-bottom: 1rem;
`;

const ErrorMessage = styled(TextNode)`
	margin-bottom: 1rem;
	word-break: break-word;
	font-family: 'Space Mono', monospace;
	color: #ddd;
`;

const ErrorStack = styled(TextNode)`
	white-space: pre-wrap;
	word-break: break-word;
	font-family: 'Space Mono', monospace;
	font-size: 0.875rem;
	max-height: 200px;
	overflow-y: auto;
	color: #ddd;
	white-space: pre-wrap;
`;

const ActionButtons = styled.div`
	display: flex;
	gap: 1rem;
	justify-content: center;
	flex-wrap: wrap;
`;

const RetryButton = styled.button`
	padding: 0.75rem 1.5rem;
	border-radius: 8px;
	border: none;
	background: var(--primary-color);
	color: var(--text-on-primary);
	font-family: 'Space Mono', monospace;
	font-weight: 500;
	cursor: pointer;
	transition: all 0.2s ease;

	&:hover {
		background: var(--primary-color-hover);
		transform: translateY(-1px);
	}

	&:active {
		transform: translateY(0);
	}
`;

const RefreshButton = styled.button`
	padding: 0.75rem 1.5rem;
	border-radius: 8px;
	border: 1px solid var(--border-primary);
	background: transparent;
	color: var(--text-paragraph);
	font-family: 'Space Mono', monospace;
	font-weight: 500;
	cursor: pointer;
	transition: all 0.2s ease;

	&:hover {
		background: var(--background-hover);
		border-color: var(--border-hover);
		transform: translateY(-1px);
	}

	&:active {
		transform: translateY(0);
	}
`;
